import React, { useMemo, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { OpenInNew } from '@mui/icons-material';

// Types
import { TCustomGFIDKPProps } from '@/@types/views/map';
import { ClosingDetails } from '@/@types/components/authFormController';
import { DataResponse, DCRecord } from '@/@types/lib/dataController';

// Custom
import MapCardActionButton from '@/ui/MapCard/MapCardActionButton';
import DataController from '@/lib/DataController';
import modelAktivnosti from '@/models/cesta_put_aktivnosti';
import UserContext from '@/context/UserContext/UserContext';
import Permissions from '@/lib/permissions';
import ModelTable from '@/ui/Table/ModelTable';
import TableButtonDelete from '@/ui/Table/ActionButtons/TableButtonDelete';
import TableButtonEdit from '@/ui/Table/ActionButtons/TableButtonEdit';
import GeneralDialog from '@/ui/Dialog/GeneralDialog';

// Context
import DialogContext from '@/context/DialogContext/DialogContext';

const AktivnostiOdrzavanjaButton = (props: TCustomGFIDKPProps) => {
  const identifier = 'aktivnosti';

  const userContext = useContext(UserContext);
  const { showDialog, showConfirmDialog, updateDialogProps, component1 } = useContext(DialogContext);

  const canEdit = userContext?.hasAnyPermission([Permissions.CestovnaInfrastrukturaEdit]);

  const { t } = useTranslation();

  const { record, onToggle } = props;

  const [records, setRecords] = useState<DCRecord[]>([]);

  const recordId = useMemo(() => (record ? (record.id ? record.id : record.fid ? record.fid : null) : null), [record]);

  const dcAktivnostiOdrzavanja = useMemo(
    () => new DataController(modelAktivnosti, `cestovna-infrastruktura/paths/${recordId}/maintenance-activities`),
    [recordId]
  );

  const refreshRecords = () => {
    dcAktivnostiOdrzavanja.GetData().then((resp: DataResponse) => {
      if (resp.success && Array.isArray(resp.data)) {
        setRecords(resp.data);
      }
    });
  };

  useEffect(() => {
    refreshRecords(); // fetch records on first render
  }, []);

  const handleDialogClose = (result: ClosingDetails) => {
    switch (result.action) {
      case 'insert':
      case 'update':
      case 'delete':
        if (result.dataChanged) refreshRecords(); // only refresh if something changed
        break;
      case 'cancel': // if user canceled don't refresh
      default:
        break;
    }
  };

  const handleEdit = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const activeRecord: DCRecord = sel[selId];

    if (activeRecord?.id) {
      showDialog(
        GeneralDialog,
        {
          dc: dcAktivnostiOdrzavanja,
          mode: 'update',
          form: 'gfi',
          initialRecord: activeRecord,
          onClose: handleDialogClose,
          title: `${t('titles.aktivnosti_odrzavanja')} - ${t('common.update')}`,
          columns: 2
        },
        2
      );
    }
  };

  const handleDelete = async (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const activeRecord = sel[Object.keys(sel)[0]]; // TODO handle multiple?

    const confirmResult = await showConfirmDialog({
      title: t('titles.aktivnosti_odrzavanja') as string,
      text: `${t('cdialogs.are_you_sure')}`,
      confirmButtonText: t('buttons.confirm') as string,
      cancelButtonText: t('buttons.cancel') as string,
    });

    if (confirmResult.success && activeRecord.id) {
      await dcAktivnostiOdrzavanja.DeleteRecord(activeRecord.id as number);
      refreshRecords();
    }
  };

  const handleAdd = () => {
    showDialog(
      GeneralDialog,
      {
        dc: dcAktivnostiOdrzavanja,
        mode: 'insert',
        form: 'default',
        onClose: handleDialogClose,
        title: `${t('titles.aktivnosti_odrzavanja')} - ${t('common.insert')}`,
        initialRecord: { path_id: recordId },
        columns: 2
      },
      2
    );
  };

  const aktivnostiTable = useMemo(() => (
      <ModelTable
        allowExport
        title="titles.aktivnosti_odrzavanja"
        size="small"
        tableName="aktivnosti"
        viewName="default"
        records={records}
        dc={dcAktivnostiOdrzavanja}
        allowSelection="one"
        allowAdd={canEdit}
        allowRowAction={() => false}
        onRowAction={() => {}}
        disableControls={false}
        handleAdd={handleAdd}
      >
        {canEdit ? <TableButtonDelete variant="outlined" onClick={handleDelete} /> : null}
        {canEdit ? <TableButtonEdit variant="outlined" onClick={handleEdit} /> : null}
      </ModelTable>
    ), [records, canEdit, dcAktivnostiOdrzavanja]);

  useEffect(() => {
    if (component1) {
      updateDialogProps(1, { body: aktivnostiTable });
    }
  }, [aktivnostiTable]);

  const handleToggleAktivnosti = () => {
    onToggle(aktivnostiTable, identifier);
  };

  return (
    <MapCardActionButton startIcon={<AssignmentIndIcon />} onClick={handleToggleAktivnosti} endIcon={<OpenInNew />}>
      {t('titles.odrzavanje')}
    </MapCardActionButton>
  );
};

export default AktivnostiOdrzavanjaButton;
